import { Option } from '../option.js';
import { RecordKind } from '../record.js';

export type CompatibleValues = number[] | string[] | boolean[] | Option[];
export type CompatibleValue = CompatibleValues[0];
export type FlatCompatibleValue = number | string | boolean;

export type CompatibilityResult = NoRestrictions | RestrictedTo;
export type CompatibleInnerValue<T extends CompatibleValues> = T extends Array<infer V> ? V : never;

export function isOption(value: CompatibleValue): value is Option {
  return typeof (value) === 'object' && '_id' in value && value.kind === RecordKind.Option;
}

// All existing values are compatible
export interface NoRestrictions {
  kind: 'NO_RESTRICTIONS'
}

// Only subset of values is compatible
export interface RestrictedTo {
  kind: 'RESTRICTED_TO',
  values: number[] | boolean[] | string[] | Option[]
}

export function isNoRestriction(vs: CompatibilityResult): vs is NoRestrictions {
  return vs.kind == 'NO_RESTRICTIONS'
}

export function isRestrictedTo(vs: CompatibilityResult): vs is RestrictedTo {
  return vs.kind === 'RESTRICTED_TO';
}

export function noRestrictions() {
  return {
    kind: 'NO_RESTRICTIONS'
  } as const;
}

export function restrictedTo(values: CompatibleValues): RestrictedTo {
  return {
    kind: 'RESTRICTED_TO',
    values
  }
}


function isValuesCompatibleValues(values: CompatibleValue[]): values is CompatibleValues {
  if (values.length <= 1) {
    return true;
  }
  const firstValueType = typeof values[0];
  return values.slice(1).every(v => typeof v === firstValueType);
}

export function castCompatibleValue(compatibleValues: CompatibleValue[]): CompatibleValues {
  if (isValuesCompatibleValues(compatibleValues)) {
    return compatibleValues;
  } else {
    throw new Error('Incompatible results of compatible values');
  }
}
